/**
 * TrustOntologyView — the Prove7 Agent Trust Ontology (G-4).
 *
 * Two axes, one picture:
 *  • the agent's seven proves (Discovery → Identity → Intent → RBAC → Trust →
 *    Enforcement → Audit) on a directional thread that flows forward and seals;
 *  • the human accountability loop: a named cast opens the journey at Discovery
 *    (creates/registers) and the SEALED run returns to the human at Audit for
 *    verification/compliance — all under the enterprise canopy.
 *
 * Wired to real data: GET /trust-graph/subjects/{id} (proves + signed envelopes),
 * the live trust snapshot (Prove7 score at the hub), and
 * /trust-graph/subjects/{id}/stewardship (accountable owner + creator + RBAC role-holders).
 *
 * Colors derive from the platform style guide (the --v2-* design tokens in
 * styles/v2-tokens.css + redesign-themes.css) — never hardcoded — so the view
 * tracks light / dark / brand themes. The accent IS the platform trust colour
 * (--v2-accent-trust). The whole view is width-capped so it never balloons.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Users, Lock } from 'lucide-react';
import { getTrustGraphSubject, getTrustGraphStewardship, getTrustGraphRuns } from '../../../services/api';
import { displayScore, publishedBandStyle } from '../../../services/trustBands';

/* Platform design tokens (theme-aware). No hardcoded colours. */
const PAL = {
  ink: 'var(--v2-text-primary)', ink2: 'var(--v2-text-secondary)', muted: 'var(--v2-text-muted)', faint: 'var(--v2-text-muted)',
  line: 'var(--v2-border-subtle)', line2: 'var(--v2-border-default)', linePri: 'var(--v2-border-strong)',
  surface: 'var(--v2-bg-surface)', surfaceMuted: 'var(--v2-bg-muted)',
  accent: 'var(--v2-accent-trust)', accentBg: 'var(--v2-accent-trust-soft)', accentBorder: 'var(--v2-accent-trust)',
  success: 'var(--v2-success)',
};
/* Style-guide mono stack (tailwind fontFamily 'mono-v2'); no CSS var exists for it. */
const MONO = "'JetBrains Mono', 'IBM Plex Mono', 'SF Mono', monospace";

const PROVES = [
  { id: 'DISCOVERY',   label: 'Discovery',  title: 'Discovery & Registration', x: 330, y: 140, nodes: ['Agent', 'Registration', 'Capability'],            seals: 'the agent is registered' },
  { id: 'IDENTITY',    label: 'Identity',   title: 'Identity',                 x: 447, y: 196, nodes: ['WIMSE id', 'SVID / credential', 'DelegationToken'], seals: 'an SVID is issued' },
  { id: 'INTENT',      label: 'Intent',     title: 'Intent',                   x: 476, y: 323, nodes: ['IntentDecision', 'ModelEvidence'],                  seals: 'intent is authorized' },
  // Display label is "Access" (matches the Identity→Intent→Access journey step);
  // id stays 'RBAC' — it's the backend TrustAssertion DOMAIN key used for prove
  // sealing/ordering. Changing it would break the prove→assertion mapping.
  { id: 'RBAC',        label: 'Access',     title: 'Access',                   x: 395, y: 425, nodes: ['Role', 'Permission', 'Scope'],                      seals: 'a role is bound' },
  // Same rule as RBAC above: the id is the sealed domain, the label is what a
  // person reads. 05 is 'Promote' because clearing the Build·Decide·Act gate is
  // what the score is for, and 06 is the verb rather than the noun.
  { id: 'TRUST',       label: 'Promote',    title: 'Promote',                  x: 265, y: 425, nodes: ['TrustAssessment', 'ConformanceScore'],              seals: 'trust is scored' },
  { id: 'ENFORCEMENT', label: 'Enforce',    title: 'Enforce',                  x: 184, y: 323, nodes: ['EnforcementRule', 'TrustAction'],                   seals: 'a governed call is enforced' },
  { id: 'AUDIT',       label: 'Audit',      title: 'Audit',                    x: 213, y: 196, nodes: ['AuditEnvelope'],                                    seals: 'any assertion is sealed' },
];
const ORDER = PROVES.map((p) => p.id);
const CX = 330, CY = 290, HUBR = 46, HUBC = 2 * Math.PI * HUBR;

function fmtTime(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch (_) { return String(v); } }

/* Memoized SVG children — hovering a node only flips `on`/`isLit` on the one or
 * two nodes whose highlight changes, so with React.memo + a stable onEnter only
 * those re-render instead of the whole 30-element SVG re-reconciling every
 * mouse-enter (the "the whole page refreshes on hover" jank). */
const ProveNode = React.memo(function ProveNode({ p, on, isSealed, onEnter }) {
  return (
    <g style={{ cursor: 'pointer' }} onMouseEnter={() => onEnter(p.id)} onClick={() => onEnter(p.id)}>
      <circle cx={p.x} cy={p.y} r="28" fill={on ? PAL.accentBg : PAL.surfaceMuted} stroke={on ? PAL.accent : PAL.line2} strokeWidth={on ? 2.5 : 1.5} opacity={isSealed ? 1 : 0.6} />
      {isSealed && <circle cx={p.x - 16} cy={p.y - 14} r="3.5" fill={PAL.success} />}
      {p.id === 'ENFORCEMENT' ? (
        <>
          <text x={p.x} y={p.y - 2} textAnchor="middle" fontSize="10.5" fill={PAL.ink}>Enforce-</text>
          <text x={p.x} y={p.y + 10} textAnchor="middle" fontSize="10.5" fill={PAL.ink}>ment</text>
        </>
      ) : (<text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="11.5" fill={p.id === 'AUDIT' ? PAL.accent : PAL.ink}>{p.label}</text>)}
    </g>
  );
});

const EdgeLine = React.memo(function EdgeLine({ p, on, isLit }) {
  return (
    <line x1={CX} y1={CY} x2={p.x} y2={p.y}
          style={{ stroke: isLit ? PAL.accent : PAL.muted, strokeWidth: isLit ? 2 : 1.5,
                   strokeDasharray: '2 4', opacity: on ? 0.9 : 0, transition: 'opacity .25s' }} />
  );
});

export default function TrustOntologyView({ agentId, agentName, score, tier, snap }) {
  const [data, setData] = useState(undefined);
  const [cast, setCast] = useState(undefined);
  const [active, setActive] = useState('SCORE');
  const [lit, setLit] = useState(() => new Set());
  const [returnOn, setReturnOn] = useState(false);
  const [status, setStatus] = useState('human opens at Discovery · the sealed run returns to the human at Audit');
  const timer = useRef(null);
  // Replay run-picker: which run (correlationId) to replay + show per-stage; null = latest.
  const [selectedRun, setSelectedRun] = useState(null);
  const [runPickerOpen, setRunPickerOpen] = useState(false);
  const [runPage, setRunPage] = useState(0);
  // G-4 backend runs list (authoritative, 30d window + pagination). Falls back to
  // grouping the loaded timeline if the endpoint is unavailable.
  const [apiRuns, setApiRuns] = useState(null);

  useEffect(() => {
    if (!agentId) return;
    let alive = true;
    setData(undefined); setCast(undefined); setLit(new Set()); setReturnOn(false); setActive('SCORE');
    setSelectedRun(null); setRunPickerOpen(false); setRunPage(0); setApiRuns(null);
    getTrustGraphSubject(agentId).then((r) => { if (alive) setData(r && r.ok !== false ? (r.data || r) : null); }).catch(() => alive && setData(null));
    getTrustGraphStewardship(agentId).then((r) => { if (alive) setCast(r && r.ok !== false ? (r.data || r) : null); }).catch(() => alive && setCast(null));
    // Authoritative runs list (30d, paginated server-side); null on failure → FE-grouping fallback.
    getTrustGraphRuns(agentId, { limit: 200 })
      .then((r) => { if (alive) { const d = r && r.ok !== false ? (r.data || r) : null; setApiRuns(Array.isArray(d?.runs) ? d.runs : null); } })
      .catch(() => { if (alive) setApiRuns(null); });
    return () => { alive = false; if (timer.current) clearInterval(timer.current); };
  }, [agentId]);

  const byDomain = useMemo(() => {
    const m = {}; const tl = Array.isArray(data?.timeline) ? data.timeline : [];
    for (const a of tl) { if (!a.domain) continue; (m[a.domain] = m[a.domain] || []).push(a); }
    return m;
  }, [data]);

  const sealedCount = Object.keys(byDomain).length;
  const rated = typeof score === 'number' && !Number.isNaN(score);
  // Fill the arc from the number actually shown, so the ring cannot be drawn at a
  // fraction its own label does not claim.
  const arc = rated ? ((displayScore(score) ?? 0) / 100) * HUBC : 0;
  const enterprise = cast?.enterprise || 'enterprise';
  const castCount = (cast?.owner ? 1 : 0) + (cast?.creator ? 1 : 0) + (cast?.roles || []).reduce((n, g) => n + (g.members?.length || 0), 0);
  // Enterprise canopy label (building icon + text), approximately centred.
  const orgLabel = `${enterprise} · enterprise oversight`;
  const labelStart = 330 - (Math.min(320, orgLabel.length * 6.3) + 16) / 2;

  // Default ("no hover") right-pane content: the Prove7 Agent Trust Score breakdown.
  const scoreMetrics = (() => {
    const m = [['Proves sealed', `${sealedCount} / 7`], ['Assertions', String(data?.assertionCount ?? 0)]];
    if (snap) {
      if (snap.conformanceMean != null) m.push(['Conformance', Number(snap.conformanceMean).toFixed(2)]);
      if (snap.totalEvaluations != null) m.push(['Evaluations', String(snap.totalEvaluations)]);
      if (snap.weightedViolations != null) m.push(['Weighted violations', String(snap.weightedViolations)]);
      if (snap.windowDays != null) m.push(['Window', `${snap.windowDays}d`]);
      if (snap.recommendedAutonomy) m.push(['Recommended autonomy', String(snap.recommendedAutonomy)]);
    }
    return m;
  })();

  /* Distinct runs (correlationId), newest first, for the run picker. Prefer the
   * authoritative backend list (30d window + server-side pagination + proveCount);
   * fall back to grouping the loaded subject timeline if the endpoint is
   * unavailable, so the picker still works on older builds. */
  const runs = useMemo(() => {
    if (Array.isArray(apiRuns)) {
      return apiRuns.map((r) => ({
        corr: r.correlationId,
        start: r.startedAt ? new Date(r.startedAt).getTime() : 0,
        end: r.endedAt ? new Date(r.endedAt).getTime() : 0,
        domains: new Set(Array.isArray(r.domains) ? r.domains : []),
        count: r.assertionCount ?? 0,
      }));
    }
    const tl = Array.isArray(data?.timeline) ? data.timeline : [];
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const m = new Map();
    for (const a of tl) {
      if (!a.correlationId) continue;
      const t = a.issuedAt ? new Date(a.issuedAt).getTime() : 0;
      if (t && t < cutoff) continue;
      let r = m.get(a.correlationId);
      if (!r) { r = { corr: a.correlationId, start: t || Number.MAX_SAFE_INTEGER, end: t || 0, domains: new Set(), count: 0 }; m.set(a.correlationId, r); }
      r.domains.add(a.domain); r.count += 1;
      if (t) { r.start = Math.min(r.start, t); r.end = Math.max(r.end, t); }
    }
    return [...m.values()].sort((x, y) => y.end - x.end);
  }, [apiRuns, data]);

  /* Run summary — a professional, evidence-backed digest of ONE selected run,
   * derived entirely from the run's sealed assertions (no fabrication): which of
   * the 7 proves sealed + when, the governed-call decisions (from the ENFORCEMENT
   * proves' signed claims: decision / tool / rule / violation), span, and the
   * hash-chain seq range. Shown in the right pane when a run is selected or the
   * Prove7 hub is clicked. */
  const runSummary = useMemo(() => {
    if (!selectedRun) return null;
    const tl = Array.isArray(data?.timeline) ? data.timeline : [];
    const rows = tl.filter((a) => a.correlationId === selectedRun);
    if (rows.length === 0) return null;
    const byDom = {};
    for (const a of rows) { if (a.domain) (byDom[a.domain] = byDom[a.domain] || []).push(a); }
    const times = rows.map((a) => (a.issuedAt ? new Date(a.issuedAt).getTime() : 0)).filter(Boolean);
    const start = times.length ? Math.min(...times) : 0;
    const end = times.length ? Math.max(...times) : 0;
    const pick = (c, keys) => { for (const k of keys) { if (c && c[k] != null && c[k] !== '') return c[k]; } return null; };
    // Governed-call decisions = every sealed assertion in the run whose SIGNED claims
    // carry a decision (the ENFORCEMENT prove has no per-call seal; the per-call
    // decision lives in the INTENT/TRUST/etc. prove claims, e.g. intent ALLOWED/BLOCKED).
    // Robust to which domain emitted it; ordered by seal time.
    const calls = rows
      .filter((a) => a.claims && pick(a.claims, ['decision', 'verdict', 'outcome']) != null)
      .map((a) => {
        const c = a.claims || {};
        return {
          ts: a.issuedAt,
          domain: a.domain,
          tool: pick(c, ['tool', 'toolName', 'skillName', 'skill', 'action']) || a.predicate,
          decision: String(pick(c, ['decision', 'verdict', 'outcome']) || '').toUpperCase(),
          rule: pick(c, ['ruleName', 'rule', 'policy', 'policyName', 'regoReason']),
          violation: pick(c, ['violation', 'reason', 'message', 'detail']),
        };
      })
      .sort((x, y) => (x.ts && y.ts ? new Date(x.ts) - new Date(y.ts) : 0));
    const seqs = rows.map((a) => a.sequenceNumber).filter((s) => s != null);
    return {
      assertions: rows.length,
      start, end,
      sealedCount: ORDER.filter((d) => (byDom[d] || []).length).length,
      proves: ORDER.map((d) => {
        const list = byDom[d] || []; const last = list[list.length - 1];
        const meta = PROVES.find((p) => p.id === d);
        return { id: d, label: meta.label, sealed: list.length > 0, predicate: last?.predicate, issuer: last?.issuer, at: last?.issuedAt, count: list.length };
      }),
      calls,
      minSeq: seqs.length ? Math.min(...seqs) : null,
      maxSeq: seqs.length ? Math.max(...seqs) : null,
    };
  }, [selectedRun, data]);

  // Decision → theme-token colour (advisory/informational vs allow vs block). No hardcoded hex.
  const decisionColor = (d) => {
    const u = String(d || '').toUpperCase();
    if (u.startsWith('ALLOW')) return 'var(--v2-success)';
    if (u.startsWith('BLOCK') || u.startsWith('DENY') || u.startsWith('REJECT')) return 'var(--v2-danger)';
    if (u.startsWith('WARN')) return 'var(--v2-warning)';
    if (u.startsWith('AUDIT')) return PAL.accent;
    return PAL.ink2;
  };

  const RUNS_PER_PAGE = 8;
  const runPageCount = Math.max(1, Math.ceil(runs.length / RUNS_PER_PAGE));
  const pagedRuns = runs.slice(runPage * RUNS_PER_PAGE, runPage * RUNS_PER_PAGE + RUNS_PER_PAGE);

  // corrOverride (explicit) → selectedRun (picker) → latest run in the timeline.
  function replay(corrOverride) {
    if (timer.current) clearInterval(timer.current);
    setLit(new Set()); setReturnOn(false);
    const tl = Array.isArray(data?.timeline) ? data.timeline : [];
    const lastCorr = corrOverride || selectedRun
      || [...tl].reverse().find((a) => a.correlationId)?.correlationId;
    const inPath = new Set((lastCorr ? tl.filter((a) => a.correlationId === lastCorr) : tl).map((a) => a.domain));
    const seq = ORDER.filter((d) => inPath.has(d));
    if (seq.length === 0) { setStatus('no sealed proof to replay yet'); return; }
    setStatus(lastCorr ? `replaying corr ${String(lastCorr).slice(0, 8)}…` : 'replaying sealed path…');
    let i = 0;
    timer.current = setInterval(() => {
      if (i < seq.length) {
        const d = seq[i]; setLit((s) => new Set(s).add(d)); setActive(d);
        setStatus(`${PROVES.find((p) => p.id === d).label}${d === 'AUDIT' ? ' · sealed →' : ' proven →'}`); i += 1;
      } else { clearInterval(timer.current); setReturnOn(true); setActive('HUMAN'); setStatus('sealed → returned to the human cast for verification · accountable to ' + enterprise); }
    }, 360);
  }

  const nodeOn = (id) => active === id || lit.has(id);
  // Stable so the memoized ProveNode children don't re-render on every hover.
  const onEnter = useCallback((id) => setActive(id), []);

  /* Static SVG scaffold — the canopy, enterprise label, orbit ring, flow thread,
   * arrowhead, the creates→ arrow and the bottom caption. None of these depend on
   * `active`/`lit`, so memoize them once (per agent/org) instead of recreating
   * ~10 elements on every mouse-enter. */
  const scaffold = useMemo(() => (
    <>
      <path d="M150 116 A 254 254 0 0 1 510 116" fill="none" stroke={PAL.accent} strokeDasharray="2 6" opacity="0.5" />
      <g transform={`translate(${labelStart}, 9)`} fill={PAL.accent}>
        <rect x="0" y="2" width="11" height="13" rx="1" />
        <rect x="2.3" y="4.3" width="2" height="2" fill={PAL.surface} />
        <rect x="6.7" y="4.3" width="2" height="2" fill={PAL.surface} />
        <rect x="2.3" y="8.3" width="2" height="2" fill={PAL.surface} />
        <rect x="6.7" y="8.3" width="2" height="2" fill={PAL.surface} />
      </g>
      <text x={labelStart + 16} y="20" textAnchor="start" fontSize="11.5" fontFamily={MONO} fill={PAL.accent}>{orgLabel}</text>
      <path d="M330 78 L330 110" fill="none" stroke={PAL.accent} strokeWidth="1.6" strokeDasharray="3 4" opacity="0.85" />
      <path d="M324 102 L330 113 L336 102 Z" fill={PAL.accent} />
      <text x="392" y="96" textAnchor="middle" fontSize="10.5" fontFamily={MONO} fill={PAL.accent}>creates · registers</text>
      <circle cx={CX} cy={CY} r="205" fill="none" stroke={PAL.line} strokeDasharray="5 5" opacity="0.7" />
      <path className="tov2arc" d="M330 140 A 150 150 0 1 1 213 196" fill="none" stroke={PAL.line2} strokeWidth="1.6" strokeDasharray="1 7" strokeLinecap="round" opacity="0.8" />
      <path d="M200 210 L196 224 L186 214 Z" fill={PAL.linePri} />
      <text x="330" y="530" textAnchor="middle" fontSize="11" fontFamily={MONO} fill={PAL.faint}>human creates → agent proves → audit seals → returns to human · on behalf of {enterprise}</text>
    </>
  ), [labelStart, orgLabel, enterprise]);

  const ap = PROVES.find((p) => p.id === active);
  // When a run is selected, the per-stage detail shows ONLY that run's evidence
  // (so "what happened at this stage during that run" is exact); otherwise all.
  const aAssertions = (byDomain[active] || [])
    .filter((x) => !selectedRun || x.correlationId === selectedRun);
  const aSealed = aAssertions.length > 0;
  const aPreds = [...new Set(aAssertions.map((x) => x.predicate))];
  const aLatest = aAssertions.length ? aAssertions[aAssertions.length - 1] : null;
  const envelope = aLatest ? {
    predicate: aLatest.predicate, issuer: aLatest.issuer, domain: active,
    claims: aLatest.claims ?? null, sequenceNumber: aLatest.sequenceNumber,
    envelopeHash: aLatest.envelopeHash, previousHash: aLatest.previousHash,
    issuedAt: aLatest.issuedAt, correlationId: aLatest.correlationId,
  } : null;

  const chip = (t) => (<span key={t} style={{ display: 'inline-block', fontFamily: MONO, fontSize: 11, color: PAL.ink2, border: `0.5px solid ${PAL.line}`, borderRadius: 6, padding: '2px 7px', margin: '3px 5px 0 0' }}>{t}</span>);

  return (
    <div>
      <style>{`@keyframes tov2flow{to{stroke-dashoffset:-160}} .tov2arc{animation:tov2flow 7s linear infinite}`}</style>

      {/* The "AGENTIC TRUST INFRASTRUCTURE · TRUST-BASED ACTIONS" strip that used to sit here
          is rendered by TrustGraphTab, the one component that mounts this view. Both drew it, so
          the page carried the same heading twice, one line apart. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
          {/* Run picker — choose any run from the last 30 days to replay + inspect
              its per-stage evidence. Built from the subject timeline (grouped by
              correlationId); empty until the agent has sealed at least one run. */}
          <button onClick={() => setRunPickerOpen((o) => !o)} disabled={runs.length === 0}
            style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: `0.5px solid ${PAL.line2}`,
                     background: PAL.surface, color: runs.length ? PAL.ink : PAL.muted,
                     cursor: runs.length ? 'pointer' : 'default' }}>
            {selectedRun ? `run ${String(selectedRun).slice(0, 8)}` : 'latest run'}{runs.length ? ' ▾' : ''}
          </button>
          {runPickerOpen && runs.length > 0 && (
            <div style={{ position: 'absolute', top: '112%', right: 86, zIndex: 30, width: 300, maxWidth: '80vw',
                          background: PAL.surface, border: `0.5px solid ${PAL.line2}`, borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 6 }}>
              <div style={{ fontSize: 10.5, color: PAL.muted, fontFamily: MONO, padding: '4px 8px' }}>
                {runs.length} run{runs.length === 1 ? '' : 's'} · last 30 days
              </div>
              <button onClick={() => { setSelectedRun(null); setRunPickerOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '6px 8px',
                         borderRadius: 6, border: 'none', background: selectedRun === null ? PAL.accentBg : 'transparent',
                         color: PAL.ink, cursor: 'pointer' }}>Latest run (default)</button>
              {pagedRuns.map((r) => (
                <button key={r.corr} onClick={() => { setSelectedRun(r.corr); setRunPickerOpen(false); setActive('SCORE'); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '6px 8px',
                           borderRadius: 6, border: 'none', background: selectedRun === r.corr ? PAL.accentBg : 'transparent',
                           color: PAL.ink, cursor: 'pointer' }}>
                  <span style={{ fontFamily: MONO }}>{String(r.corr).slice(0, 8)}</span>
                  <span style={{ color: PAL.muted }}> · {r.domains.size}/7 proves · {fmtTime(r.end)}</span>
                </button>
              ))}
              {runPageCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', fontSize: 11, color: PAL.ink2 }}>
                  <button onClick={() => setRunPage((p) => Math.max(0, p - 1))} disabled={runPage === 0}
                    style={{ background: 'none', border: 'none', cursor: runPage === 0 ? 'default' : 'pointer', color: runPage === 0 ? PAL.muted : PAL.accent }}>‹ prev</button>
                  <span>{runPage + 1} / {runPageCount}</span>
                  <button onClick={() => setRunPage((p) => Math.min(runPageCount - 1, p + 1))} disabled={runPage >= runPageCount - 1}
                    style={{ background: 'none', border: 'none', cursor: runPage >= runPageCount - 1 ? 'default' : 'pointer', color: runPage >= runPageCount - 1 ? PAL.muted : PAL.accent }}>next ›</button>
                </div>
              )}
            </div>
          )}
          <button onClick={() => replay()} style={{ fontSize: 12.5, padding: '5px 12px', borderRadius: 8, border: `0.5px solid ${PAL.line2}`, background: PAL.surface, color: PAL.ink, cursor: 'pointer' }}>▶ Replay {selectedRun ? 'run' : 'action'}</button>
        </div>
      </div>
      <div style={{ fontSize: 11.5, fontFamily: MONO, color: PAL.muted, marginBottom: 4 }}>
        {data === undefined ? 'loading…' : `${data?.assertionCount ?? 0} assertions · ${sealedCount}/7 proves`} · {status}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, alignItems: 'start', marginTop: 22 }}>
        {/* Detail is STICKY — it stays on the last-hovered node so the operator can
            read/scroll/interact with the signed envelope, and only changes when
            another node is hovered (no reset-to-score on mouseleave). */}
        <div style={{ minWidth: 0, paddingTop: 8 }}>
        <svg viewBox="0 0 660 545" width="100%" role="img" aria-label="Prove7 trust ontology with human accountability" style={{ display: 'block', maxWidth: 600, margin: '0 auto' }}>

          {scaffold}

          <g style={{ cursor: 'pointer' }} onMouseEnter={() => onEnter('HUMAN')} onClick={() => onEnter('HUMAN')}>
            <circle cx="330" cy="50" r="27" fill={PAL.accentBg} stroke={PAL.accent} strokeWidth={active === 'HUMAN' ? 2.5 : 1.5} opacity={active === 'HUMAN' ? 1 : 0.85} />
            {/* Human = person icon (the accountable cast) */}
            <circle cx="330" cy="44" r="4.8" fill={PAL.accent} />
            <path d="M322.5 58 a 7.5 7.5 0 0 1 15 0 Z" fill={PAL.accent} />
            <text x="330" y="71" textAnchor="middle" fontSize="8.5" fill={PAL.accent}>{castCount || '—'}</text>
          </g>

          {/* Return loop — only this arc depends on returnOn/active, so it stays inline. */}
          <path d="M206 174 C 150 96, 232 52, 304 56" fill="none" stroke={PAL.accent} strokeWidth={returnOn || active === 'HUMAN' ? 2.4 : 1.6} strokeDasharray="3 4" opacity={returnOn || active === 'HUMAN' ? 1 : 0.3} style={{ transition: 'opacity .3s' }} />
          <path d="M298 50 L309 56 L298 62 Z" fill={PAL.accent} opacity={returnOn || active === 'HUMAN' ? 1 : 0.4} />
          <text x="196" y="116" textAnchor="middle" fontSize="10.5" fontFamily={MONO} fill={PAL.accent}>verifies · ratifies · compliance</text>

          {PROVES.map((p) => (
            <EdgeLine key={`e-${p.id}`} p={p} on={active === p.id || lit.has(p.id)} isLit={lit.has(p.id)} />
          ))}

          {PROVES.map((p) => (
            <ProveNode key={p.id} p={p} on={nodeOn(p.id)}
                       isSealed={(byDomain[p.id] || []).length > 0} onEnter={onEnter} />
          ))}

          {/* Center Prove7 score node — click OR hover to show the score breakdown
              (this is how you return to the SCORE panel now that the detail is sticky). */}
          <g style={{ cursor: 'pointer' }} onMouseEnter={() => onEnter('SCORE')} onClick={() => onEnter('SCORE')}>
            <circle cx={CX} cy={CY} r={HUBR} fill={PAL.surface} stroke={active === 'SCORE' ? PAL.accent : PAL.line} strokeWidth="7" />
            {rated && <circle cx={CX} cy={CY} r={HUBR} fill="none" stroke={PAL.accent} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${arc.toFixed(1)} ${(HUBC - arc).toFixed(1)}`} transform={`rotate(-90 ${CX} ${CY})`} />}
            {/* displayScore, not Math.round. This hub and the score card ten lines below
                render the SAME score prop, and a raw round here showed 66 beside the
                card's 65 for one agent on one screen. trustBands.displayScore is the one
                rule (floor, clamped) and publishedBandStyle bands on its output precisely
                so the number and the band beside it can never disagree — a round here
                opts this number out of that guarantee. */}
            <text x={CX} y={CY - 5} textAnchor="middle" fontSize="29" fill={PAL.ink}>{rated ? displayScore(score) : '—'}</text>
            <text x={CX} y={CY + 13} textAnchor="middle" fontSize="12" fill={PAL.ink2}>Prove7</text>
          </g>
        </svg>
        </div>

        <div style={{ border: `0.5px solid ${PAL.line2}`, borderRadius: 12, background: PAL.surface, padding: '13px 16px', minHeight: 360, minWidth: 0 }}>
        {active === 'SCORE' ? (runSummary ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, color: PAL.ink }}>Run summary</span>
              <span style={{ fontFamily: MONO, fontSize: 11, color: PAL.accent, border: `0.5px solid ${PAL.accentBorder}`, background: PAL.accentBg, borderRadius: 8, padding: '2px 8px' }}>run {String(selectedRun).slice(0, 8)}</span>
              <span style={{ fontSize: 11, color: runSummary.sealedCount === 7 ? PAL.success : PAL.ink2, border: `0.5px solid ${PAL.line2}`, borderRadius: 8, padding: '2px 8px' }}>{runSummary.sealedCount}/7 proves sealed</span>
              <button onClick={() => setSelectedRun(null)} style={{ marginLeft: 'auto', fontSize: 11, color: PAL.muted, background: 'none', border: 'none', cursor: 'pointer' }}>clear ✕</button>
            </div>
            <div style={{ fontSize: 11.5, fontFamily: MONO, color: PAL.ink2, marginTop: 8, lineHeight: 1.6 }}>
              {fmtTime(runSummary.start)} → {fmtTime(runSummary.end)}{runSummary.start && runSummary.end ? ` · ${Math.max(0, Math.round((runSummary.end - runSummary.start) / 1000))}s` : ''}<br />
              {runSummary.assertions} sealed assertions · accountable to {enterprise}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 12 }}>
              <span style={{ fontSize: 34, color: PAL.ink, lineHeight: 1 }}>{rated ? displayScore(score) : '—'}</span>
              <span style={{ fontSize: 12, color: PAL.muted }}>
                / 100 · Prove7 trust{rated ? ` · ${publishedBandStyle(score).label}` : ''}
              </span>
            </div>
            <div style={{ fontSize: 11, color: PAL.muted, marginTop: 14, marginBottom: 4 }}>seven proves · this run</div>
            {runSummary.proves.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 11.5, padding: '3px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                <span style={{ width: 10, color: p.sealed ? PAL.success : PAL.muted }}>{p.sealed ? '●' : '○'}</span>
                <span style={{ minWidth: 92, color: p.sealed ? PAL.ink : PAL.muted }}>{p.label}</span>
                <span style={{ fontFamily: MONO, color: PAL.ink2 }}>{p.sealed ? (p.predicate || 'sealed') : '—'}</span>
                {p.count > 1 && <span style={{ color: PAL.muted }}>×{p.count}</span>}
                <span style={{ marginLeft: 'auto', fontFamily: MONO, color: PAL.muted }}>{p.sealed ? fmtTime(p.at) : ''}</span>
              </div>
            ))}
            {runSummary.calls.length > 0 && (
              <>
                <div style={{ fontSize: 11, color: PAL.muted, marginTop: 14, marginBottom: 4 }}>sealed decisions · this run</div>
                {runSummary.calls.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 11.5, padding: '3px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                    <span style={{ minWidth: 70, fontFamily: MONO, fontSize: 10.5, fontWeight: 600, color: decisionColor(c.decision) }}>{c.decision || '—'}</span>
                    <span style={{ fontFamily: MONO, color: PAL.ink }}>{c.tool}</span>
                    {c.domain && <span style={{ fontFamily: MONO, fontSize: 10, color: PAL.muted }}>{String(c.domain).toLowerCase()}</span>}
                    {c.rule && <span style={{ color: PAL.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }} title={c.rule}>· {c.rule}</span>}
                    <span style={{ marginLeft: 'auto', fontFamily: MONO, color: PAL.muted }}>{fmtTime(c.ts)}</span>
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 11.5, color: PAL.ink2 }}>
              <Lock size={11} color={PAL.accent} aria-hidden="true" />
              <span>Hash-chained · {runSummary.assertions} sealed envelopes{runSummary.minSeq != null ? ` · seq ${runSummary.minSeq}–${runSummary.maxSeq}` : ''} · returned to the human cast for verification</span>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, color: PAL.ink }}>Prove7 Agent Trust Score</span>
              {/* The pill beside a score says what the score is. It used to say the AUTONOMY TIER
                  — a 78 is Adequate on the published scale and Restricted on the tier scale, so
                  this page labelled the same agent Restricted while the summary called it
                  Adequate, and both were right in their own terms. One of them is the band, and
                  the band is what belongs next to the number. The tier still appears, below,
                  saying what it actually governs. */}
              {rated && (
                <span style={{ fontSize: 11, color: publishedBandStyle(score).ink,
                               border: `0.5px solid ${publishedBandStyle(score).color}`,
                               background: publishedBandStyle(score).soft,
                               borderRadius: 8, padding: '2px 8px' }}>
                  {publishedBandStyle(score).label}
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: PAL.ink2 }}>{enterprise} · {agentName || cast?.agentName || 'agent'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 12 }}>
              <span style={{ fontSize: 40, color: PAL.ink, lineHeight: 1 }}>{rated ? displayScore(score) : '—'}</span>
              <span style={{ fontSize: 13, color: PAL.muted }}>/ 100 · Prove7</span>
            </div>
            {/* Only once there is a score. An unrated agent has no tier to permit, and the
                sentence came out as "Permits unrated autonomy". */}
            {rated && tier && (
              <div style={{ fontSize: 11.5, color: PAL.ink2, marginTop: 6 }}>
                Permits {String(tier).toLowerCase()} autonomy
              </div>
            )}
            <div style={{ fontSize: 12, color: PAL.ink2, marginTop: 6, lineHeight: 1.5 }}>
              {rated
                ? 'Computed from sealed conformance + enforcement evidence over the scoring window. Hover a prove to inspect its signed evidence, or the human to see who is accountable.'
                : 'Unrated — no governed evidence sealed yet. The score appears once this agent is run and scored. Hover a prove or the human to explore the ontology.'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', marginTop: 12 }}>
              {scoreMetrics.map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                  <span style={{ color: PAL.muted }}>{k}</span><span style={{ color: PAL.ink, fontFamily: MONO }}>{v}</span>
                </div>
              ))}
            </div>
          </>
        )) : active === 'HUMAN' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={15} color={PAL.accent} aria-hidden="true" />
              <span style={{ fontSize: 16, color: PAL.ink }}>Human accountability</span>
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: PAL.ink2 }}>{enterprise} · {agentName || cast?.agentName || 'agent'}</span>
            </div>
            <div style={{ fontSize: 11, color: PAL.muted, margin: '9px 0 2px' }}>accountable humans for this agent &amp; the current run</div>
            {cast === undefined ? (<div style={{ fontSize: 12, color: PAL.muted }}>loading the cast…</div>) : (
              <>
                {/* Accountable owner — the primary human accountability entry, shown first and highlighted. */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                  <span style={{ minWidth: 128, fontSize: 11, color: PAL.accent, textTransform: 'uppercase', letterSpacing: '.03em', fontWeight: 600 }}>Accountable owner</span>
                  <span style={{ minWidth: 120, fontSize: 12.5, color: cast?.owner?.name ? PAL.ink : PAL.muted }}>{cast?.owner?.name || 'Not yet accepted'}</span>
                  <span style={{ fontSize: 11, color: PAL.ink2, fontFamily: MONO }}>{cast?.owner?.name ? 'accepted accountability (A)' : 'nomination pending — no accountable owner'}</span>
                </div>
                {cast?.creator && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                    <span style={{ minWidth: 128, fontSize: 11, color: PAL.muted, textTransform: 'uppercase', letterSpacing: '.03em' }}>Agent creator</span>
                    <span style={{ minWidth: 120, fontSize: 12.5, color: PAL.ink }}>{cast.creator.name || '—'}</span>
                    <span style={{ fontSize: 11, color: PAL.ink2, fontFamily: MONO }}>registered this agent at Discovery</span>
                  </div>
                )}
                {(cast?.roles || []).map((g) => (
                  <div key={g.role} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                    <span style={{ minWidth: 128, fontSize: 11, color: PAL.muted, textTransform: 'uppercase', letterSpacing: '.03em' }}>{g.role}</span>
                    <span style={{ fontSize: 12.5, color: PAL.ink }}>{(g.members || []).map((m) => m.name).filter(Boolean).join(', ') || '—'}</span>
                  </div>
                ))}
                {castCount === 0 && (<div style={{ fontSize: 12, color: PAL.muted, marginTop: 6 }}>No human cast resolved yet for this agent's org/tenant.</div>)}
                <div style={{ fontSize: 11, color: PAL.accent, fontFamily: MONO, marginTop: 9 }}>agent acts on behalf of its human cast → on behalf of {enterprise}</div>
              </>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 16, color: PAL.ink }}>
              <span>{ap.title}</span>
              {aSealed
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, lineHeight: 1, color: PAL.accent, border: `0.5px solid ${PAL.accentBorder}`, background: PAL.accentBg, borderRadius: 8, padding: '3px 8px', whiteSpace: 'nowrap' }}><Lock size={10} /> {aAssertions.length} sealed</span>
                : <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, lineHeight: 1, color: PAL.muted, border: `0.5px solid ${PAL.line}`, borderRadius: 8, padding: '3px 8px' }}>unsealed</span>}
            </div>
            <div style={{ fontSize: 11, color: PAL.muted, marginTop: 8 }}>graph nodes</div>
            <div>{ap.nodes.map(chip)}</div>
            {aSealed ? (
              <>
                <div style={{ fontSize: 11, color: PAL.muted, marginTop: 8 }}>predicate edges</div>
                <div>{aPreds.map(chip)}</div>
                <div style={{ fontSize: 11, color: PAL.muted, marginTop: 10 }}>assertions</div>
                {aAssertions.slice().reverse().map((x, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 11, padding: '2px 0', borderBottom: `0.5px solid ${PAL.line}` }}>
                    <span style={{ color: PAL.ink, minWidth: 150 }}>{x.predicate}</span>
                    <span style={{ color: PAL.ink2 }}>{x.issuer}</span>
                    {x.correlationId && <span style={{ color: PAL.muted }}>· corr {String(x.correlationId).slice(0, 8)}</span>}
                    <span style={{ marginLeft: 'auto', color: PAL.muted }}>{fmtTime(x.issuedAt)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 11, color: PAL.muted }}>signed envelope</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: PAL.muted }}>seq {aLatest.sequenceNumber ?? '—'} · hash-chained</span>
                </div>
                <pre style={{ fontFamily: MONO, fontSize: 11, color: PAL.ink2, background: PAL.surfaceMuted, borderRadius: 8, padding: '9px 11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.55, margin: '4px 0 0' }}>{JSON.stringify(envelope, null, 1)}</pre>
              </>
            ) : (
              <div style={{ fontSize: 12, color: PAL.muted, marginTop: 12 }}>No proof sealed yet — this prove seals when {ap.seals}. The audit chain wraps every prove as it lands.</div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
